import * as React from "react";
import { Button } from "../../ui/button.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CookBottomBarProps {
  onBack: () => void;
  onDoneStep: () => void;
  onNext: () => void;
  canBack: boolean;
  canNext: boolean;
}

// ---------------------------------------------------------------------------
// CookBottomBar
// ---------------------------------------------------------------------------

export function CookBottomBar({
  onBack,
  onDoneStep,
  onNext,
  canBack,
  canNext,
}: CookBottomBarProps) {
  // Keyboard navigation: ArrowLeft = back, ArrowRight = next.
  // Guard: ignore when focus is in an editable element so typing notes doesn't navigate.
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const t = e.target as HTMLElement;
      if (
        t.tagName === "INPUT" ||
        t.tagName === "TEXTAREA" ||
        t.isContentEditable
      ) {
        return;
      }

      if (e.key === "ArrowRight" && canNext) {
        onNext();
      } else if (e.key === "ArrowLeft" && canBack) {
        onBack();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onBack, onNext, canBack, canNext]);

  return (
    <div className="flex items-center justify-between gap-2 bg-zinc-900 px-4 pb-6 pt-3">
      {/* ← Back */}
      <Button
        size="cook"
        variant="outline"
        aria-label="Back"
        disabled={!canBack}
        onClick={onBack}
      >
        ← Back
      </Button>

      {/* ✓ Done this step — primary emerald, centre */}
      <Button
        size="cook"
        variant="default"
        className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500/50"
        onClick={onDoneStep}
      >
        ✓ Done this step
      </Button>

      {/* Next → */}
      <Button
        size="cook"
        variant="outline"
        aria-label="Next"
        disabled={!canNext}
        onClick={onNext}
      >
        Next →
      </Button>
    </div>
  );
}
